import React from 'react';
import ReactDOM from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchCoreMetrics } from '../services/analyticsService';
import { useSocialConnections } from '../hooks/useSocialConnections';
import { useConnectedAccountsLimit } from '../hooks/useConnectedAccountsLimit';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { ArrowUp, ArrowDown, Facebook, Instagram, Twitter, Linkedin, Youtube, Link2, X, Users, TrendingUp, RefreshCw, AlertCircle, Zap, Crown, Hourglass } from 'lucide-react';
import { Button } from './ui/button';
import ConfirmationDialog from './dialog/ConfirmationDialog';
import TikTokIcon from './icons/TikTokIcon';
import { useToast } from '../hooks/use-toast';
import { motion, AnimatePresence } from 'framer-motion';
import InstagramRequirementsModal from './modals/InstagramRequirementsModal';

const platformConfig: { [key: string]: { icon: React.ElementType, color: string, bgColor: string } } = {
  Facebook: { icon: Facebook, color: 'text-white', bgColor: 'bg-blue-600' },
  Instagram: { icon: Instagram, color: 'text-white', bgColor: 'bg-pink-600' },
  X: { icon: X, color: 'text-white', bgColor: 'bg-black' },
  LinkedIn: { icon: Linkedin, color: 'text-white', bgColor: 'bg-blue-700' },
  YouTube: { icon: Youtube, color: 'text-white', bgColor: 'bg-red-600' },
  TikTok: { icon: TikTokIcon, color: 'text-white', bgColor: 'bg-black' },
  Pinterest: { icon: () => null, color: 'text-white', bgColor: 'bg-red-700' },
  'Google Business Profile': { icon: () => null, color: 'text-white', bgColor: 'bg-green-600' },
};

const PlatformMetricsCards: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { 
    connections, 
    isLoading: connectionsLoading, 
    error: connectionsError, 
    handleConnect, 
    handleDisconnect, 
    isConnected, 
    isDisconnecting,
    showInstagramModal,
    setShowInstagramModal,
    proceedWithInstagramConnection
  } = useSocialConnections();
  const { canConnect, isAtLimit, currentCount, maxLimit, isUnlimited, refetch: refetchLimits } = useConnectedAccountsLimit();

  const [disconnectDialog, setDisconnectDialog] = React.useState<{
    isOpen: boolean;
    platform: string;
    platformName: string;
  }>({
    isOpen: false,
    platform: '',
    platformName: ''
  });

  const [showUpgradeModal, setShowUpgradeModal] = React.useState(false);
  const [attemptedPlatform, setAttemptedPlatform] = React.useState<string>('');

  // Fetch real platform data using the existing analytics endpoint
  const { data: analyticsData, isLoading, error, refetch } = useQuery({
    queryKey: ['socialAnalytics', connections?.length], // Add connections length to query key
    queryFn: async () => {
      try {
        console.log('📊 Fetching social analytics data...');
        const response = await api.get('/social/analytics?days=30');
        console.log('📊 Analytics response:', response.data);
        return response.data;
      } catch (error) {
        console.error('❌ Error fetching analytics:', error);
        throw error;
      }
    },
    enabled: !!connections && connections.length > 0,
    staleTime: 5 * 60 * 1000,  // Cache for 5 minutes
    cacheTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    // No refetchInterval - data only updates once daily via scheduled job
  });

  // Refetch analytics when connections change
  React.useEffect(() => {
    if (connections && connections.length >= 0) {
      console.log('🔄 Connections changed, refetching analytics...');
      refetch();
      refetchLimits(); // Also refetch limits
    }
  }, [connections, refetch, refetchLimits]);

  // Handle connect with limit check
  const handleConnectWithLimitCheck = (provider: string, platformName: string) => {
    // Check if already connected
    if (isConnected(provider)) {
      handleConnect(provider);
      return;
    }

    // Check if at limit
    if (!canConnect) {
      setAttemptedPlatform(platformName);
      setShowUpgradeModal(true);
      return;
    }

    // Proceed with connection
    handleConnect(provider);
  };

  // Transform analytics data to platform metrics format
  const platformData = React.useMemo(() => {
    if (!analyticsData?.platforms) return [];

    console.log('📊 Raw analytics data:', analyticsData);

    return analyticsData.platforms.map((platform: any) => {
      console.log(`📊 Processing platform ${platform.platform}:`, platform);

      // Map backend platform names to display names
      const platformName = platform.platform === 'twitter' ? 'X' : platform.platform.charAt(0).toUpperCase() + platform.platform.slice(1);

      return {
        platform: platformName,
        followers: platform.followers || 0,
        engagementRate: platform.engagement_rate || 0,
        isConnected: true
      };
    });
  }, [analyticsData]);

  if (isLoading || connectionsLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {['Facebook', 'Instagram', 'X', 'YouTube'].map((platform) => (
          <div key={platform} className="bg-white/90 dark:bg-slate-800/50 backdrop-blur-xl border border-slate-200/60 dark:border-slate-600/40 rounded-xl p-4 shadow-xl">
            <div className="animate-pulse">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-9 h-9 bg-slate-200 dark:bg-slate-600 rounded-lg"></div>
                <div>
                  <div className="h-4 bg-slate-200 dark:bg-slate-600 rounded w-16 mb-1"></div>
                  <div className="h-3 bg-slate-200 dark:bg-slate-600 rounded w-20"></div>
                </div>
              </div>
              <div className="space-y-2">
                <div className="h-8 bg-slate-200 dark:bg-slate-600 rounded w-full"></div>
                <div className="h-4 bg-slate-200 dark:bg-slate-600 rounded w-3/4 mx-auto"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }
  if (error) return <div>Error loading platform metrics: {error.message}</div>;
  if (connectionsError) return <div>Error loading connections: {connectionsError.message}</div>;

  const desiredPlatforms = ['Facebook', 'Instagram'];

  // Create platform data for all desired platforms, using real data where available
  const allPlatforms = desiredPlatforms.map(platformName => {
    // First check if we have real platform data from direct API calls
    const realPlatformData = platformData?.find(p => p.platform === platformName);

    if (realPlatformData) {
      console.log(`📊 Real ${platformName} data:`, realPlatformData);
      return realPlatformData;
    }

    // Return default structure for non-connected platforms
    return {
      platform: platformName,
      followers: 0,
      engagementRate: 0,
      isConnected: false
    };
  });

  return (
    <div className="space-y-4 py-3 px-3">
      {/* Header with Limit Info */}
      <div className="flex items-center justify-between px-2">
        <div className="flex items-center space-x-3">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            Platform Connections
          </h3>
          {!isUnlimited && !connectionsLoading && maxLimit > 0 && (
            <div className={`px-3 py-1 rounded-full text-xs font-medium ${isAtLimit
              ? 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400'
              : currentCount >= maxLimit * 0.8
                ? 'bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400'
                : 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
              }`}>
              {currentCount}/{maxLimit} Connected
            </div>
          )}
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => {
              console.log('🔄 Manually refreshing platform metrics...');
              console.log('🔗 Available connections:', connections);
              refetch();
            }}
            className="flex items-center space-x-2 px-3 py-1.5 text-sm bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-700 rounded-lg transition-colors"
            disabled={isLoading}
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Limit Warning Banner - Only show if limit > 0 */}
      {isAtLimit && maxLimit > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mx-2 p-4 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-xl border border-amber-200 dark:border-amber-800"
        >
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-amber-900 dark:text-amber-100 mb-1">
                Connection Limit Reached
              </h4>
              <p className="text-sm text-amber-700 dark:text-amber-300 mb-3">
                You've connected {currentCount} out of {maxLimit} accounts available on your current plan.
                Disconnect an account or upgrade to connect more platforms.
              </p>
              <button
                onClick={() => navigate('/pricing')}
                className="inline-flex items-center space-x-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-medium transition-colors"
              >
                <Crown className="w-4 h-4" />
                <span>Upgrade Plan</span>
              </button>
            </div>
          </div>
        </motion.div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {allPlatforms.map((platformData) => {
          const config = platformConfig[platformData.platform as keyof typeof platformConfig] || { icon: () => null, color: 'text-gray-700 dark:text-gray-200', bgColor: 'bg-gray-200 dark:bg-gray-600' };
          const Icon = config.icon;
          // Map display name back to provider name for connection checks
          const providerName = platformData.platform === 'X' ? 'twitter' : platformData.platform.toLowerCase();
          const isPlatformConnected = platformData.isConnected || isConnected(providerName);
          const isDisconnectingPlatform = isDisconnecting(providerName);

          return (
            <div
              key={platformData.platform}
              className={`relative rounded-xl p-2.5 pb-1.5 shadow-lg transition-all duration-300 ${isDisconnectingPlatform
                ? 'bg-gray-400 opacity-60 cursor-not-allowed'
                : isPlatformConnected
                  ? `${config.bgColor} hover:shadow-xl`
                  : 'bg-white/80 dark:bg-slate-800 border-2 border-dashed border-slate-200/60 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500 hover:bg-white/90 dark:hover:bg-slate-700/80 backdrop-blur-sm'
                }`}
            >
              {/* Connection Status Indicator */}
              <div className="absolute top-2 right-2">
                {isDisconnectingPlatform ? (
                  <div className="w-3 h-3 bg-orange-400 rounded-full shadow-sm animate-spin border border-white"></div>
                ) : isPlatformConnected ? (
                  <div className="w-3 h-3 bg-green-400 rounded-full shadow-sm animate-pulse"></div>
                ) : (
                  <div className="w-3 h-3 bg-slate-400 dark:bg-gray-600 rounded-full"></div>
                )}
              </div>

              <div className="flex items-center space-x-3 mb-4">
                <div className={`p-1.5 rounded-lg ${isPlatformConnected ? 'bg-white/20' : 'bg-slate-100/80 dark:bg-slate-700'}`}>
                  <Icon className={`w-4 h-4 ${isPlatformConnected ? config.color : 'text-slate-600 dark:text-gray-400'}`} />
                </div>
                <div>
                  <h3 className={`text-sm font-bold ${isPlatformConnected ? config.color : 'text-slate-800 dark:text-gray-300'}`}>
                    {platformData.platform}
                  </h3>
                  <p className={`text-xs ${isPlatformConnected ? `${config.color} opacity-80` : 'text-slate-600 dark:text-gray-400'}`}>
                    {isDisconnectingPlatform ? 'Disconnecting...' : isPlatformConnected ? 'Connected' : 'Not Connected'}
                  </p>
                </div>
              </div>

              {isPlatformConnected ? (
                <div className="space-y-1">
                  {/* Metrics - Side by Side */}
                  <div className="flex justify-between items-center text-center">
                    <div className="flex flex-col items-center flex-1">
                      <div className="flex items-center space-x-1">
                        <Users className={`w-3 h-3 ${config.color} opacity-80`} />
                        <p className={`text-lg font-bold ${config.color}`}>
                          {platformData.followers ? platformData.followers.toLocaleString() : '0'}
                        </p>
                      </div>
                      <p className={`text-xs ${config.color} opacity-70`}>Followers</p>
                    </div>
                    <div className="flex flex-col items-center flex-1">
                      <div className="flex items-center space-x-1">
                        <TrendingUp className={`w-3 h-3 ${config.color} opacity-70`} />
                        <p className={`text-lg font-bold ${config.color}`}>
                          {platformData.engagementRate ? `${platformData.engagementRate.toFixed(1)}%` : '0%'}
                        </p>
                      </div>
                      <p className={`text-xs ${config.color} opacity-70`}>Engagement</p>
                    </div>
                  </div>

                  {/* Disconnect Button */}
                  <button
                    onClick={() => {
                      // Map display name back to provider name
                      const providerName = platformData.platform === 'X' ? 'twitter' : platformData.platform.toLowerCase();
                      setDisconnectDialog({
                        isOpen: true,
                        platform: providerName,
                        platformName: platformData.platform
                      });
                    }}
                    disabled={isDisconnectingPlatform}
                    className={`w-full py-1.5 px-3 bg-white/20 hover:bg-red-500/20 ${config.color} hover:text-red-100 rounded-lg transition-all duration-200 text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 mt-1`}
                  >
                    {isDisconnectingPlatform ? (
                      <>
                        <RefreshCw className="w-3 h-3 animate-spin" />
                        <span>Disconnecting...</span>
                      </>
                    ) : (
                      <>
                        <X className="w-3 h-3" />
                        <span>Disconnect</span>
                      </>
                    )}
                  </button>
                </div>
              ) : (
                <div className="space-y-1">
                  <div className="text-center py-0.5">
                    <p className="text-sm text-slate-600 dark:text-gray-400 mb-1 font-medium">
                      Connect to view metrics and manage posts
                    </p>
                  </div>

                  {/* Connect Button */}
                  <button
                    onClick={() => {
                      // If limit is 0, show upgrade modal
                      if (maxLimit === 0) {
                        setAttemptedPlatform(platformData.platform);
                        setShowUpgradeModal(true);
                        return;
                      }
                      // Map display name back to provider name
                      const providerName = platformData.platform === 'X' ? 'twitter' : platformData.platform.toLowerCase();
                      handleConnectWithLimitCheck(providerName, platformData.platform);
                    }}
                    className={`w-full py-2 px-3 rounded-lg transition-all duration-200 text-sm font-semibold flex items-center justify-center space-x-2 shadow-md ${config.bgColor} text-white hover:opacity-90 hover:scale-105 hover:shadow-lg mt-1`}
                  >
                    {maxLimit === 0 ? (
                      <>
                        <Crown className="w-4 h-4 text-yellow-300" />
                        <span>Connect {platformData.platform}</span>
                      </>
                    ) : (
                      <>
                        <Link2 className="w-4 h-4" />
                        <span>Connect {platformData.platform}</span>
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          );
        })}
        
        {/* Coming Soon Card */}
        <div className="relative rounded-xl p-2.5 pb-1.5 shadow-lg bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800/50 dark:to-slate-900/50 border-2 border-dashed border-slate-300 dark:border-slate-600">
          <div className="flex flex-col items-center justify-center h-full min-h-[100px] space-y-1">
            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
              <Hourglass className="w-3 h-3 text-white" />
            </div>
            <div className="text-center">
              <h3 className="text-xs font-semibold text-slate-900 dark:text-white mb-1">
                More Platforms Coming Soon
              </h3>
              <p className="text-xs text-slate-600 dark:text-slate-400">
                YouTube, X (Twitter), TikTok, and more!
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Disconnect Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={disconnectDialog.isOpen}
        onClose={() => setDisconnectDialog({ isOpen: false, platform: '', platformName: '' })}
        onConfirm={() => {
          handleDisconnect(disconnectDialog.platform);
          setDisconnectDialog({ isOpen: false, platform: '', platformName: '' });
        }}
        title={`Disconnect ${disconnectDialog.platformName}?`}
        message={`Are you sure you want to disconnect your ${disconnectDialog.platformName} account? This will remove access to your ${disconnectDialog.platformName} data and you'll need to reconnect to publish posts.`}
        confirmText="Disconnect"
        confirmVariant="danger"
      />

      {/* Upgrade Modal - Rendered via Portal */}
      {showUpgradeModal && ReactDOM.createPortal(
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
            onClick={() => setShowUpgradeModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white dark:bg-slate-800 rounded-2xl p-6 max-w-md w-full shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center">
                {/* Icon */}
                <div className="w-16 h-16 bg-gradient-to-br from-amber-100 to-orange-100 dark:from-amber-900/30 dark:to-orange-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Crown className="w-8 h-8 text-amber-600 dark:text-amber-400" />
                </div>

                {/* Title */}
                <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                  {maxLimit === 0 ? 'Upgrade to Connect Social Accounts' : 'Connection Limit Reached'}
                </h3>

                {/* Message */}
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  {maxLimit === 0 ? (
                    <>
                      Connecting social media accounts is available on paid plans. Upgrade now to connect your platforms and start publishing.
                    </>
                  ) : (
                    <>
                      You've reached your limit of <span className="font-semibold text-gray-900 dark:text-gray-100">{maxLimit} connected accounts</span> on your current plan.
                      {attemptedPlatform && (
                        <span className="block mt-2">
                          To connect <span className="font-semibold text-gray-900 dark:text-gray-100">{attemptedPlatform}</span>, you'll need to upgrade your plan or disconnect an existing account.
                        </span>
                      )}
                    </>
                  )}
                </p>

                {/* Current Usage - Only show if limit > 0 */}
                {maxLimit > 0 && (
                  <div className="mb-6 p-4 bg-gray-50 dark:bg-slate-700/50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Current Usage</span>
                      <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                        {currentCount}/{maxLimit} accounts
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-slate-600 rounded-full h-2">
                      <div
                        className="bg-gradient-to-r from-amber-500 to-orange-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${(currentCount / maxLimit) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                )}

                {/* Upgrade Benefits */}
                <div className="mb-6 text-left">
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
                    Upgrade to get:
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                      <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                      <span>Connect up to <strong className="text-gray-900 dark:text-gray-100">5 accounts</strong> (Pro) or <strong className="text-gray-900 dark:text-gray-100">unlimited</strong> (Business)</span>
                    </li>
                    <li className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                      <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                      <span>More monthly posts and scheduled content</span>
                    </li>
                    <li className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                      <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                      <span>Advanced analytics and insights</span>
                    </li>
                  </ul>
                </div>

                {/* Actions */}
                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowUpgradeModal(false)}
                    className="flex-1 px-4 py-2.5 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors text-sm font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      setShowUpgradeModal(false);
                      navigate('/pricing');
                    }}
                    className="flex-1 px-4 py-2.5 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white rounded-lg transition-all duration-200 text-sm font-semibold flex items-center justify-center space-x-2 shadow-md hover:shadow-lg"
                  >
                    <Crown className="w-4 h-4" />
                    <span>Upgrade Now</span>
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}

      {/* Instagram Requirements Modal */}
      <InstagramRequirementsModal
        isOpen={showInstagramModal}
        onClose={() => setShowInstagramModal(false)}
        onProceed={proceedWithInstagramConnection}
      />
    </div>
  );
};

export default PlatformMetricsCards;
